import { compareRecordedDates, recordedTime } from '@/utils/recordedValues'
/**
 * Mock Audit Log Data
 * Sample audit entries for development and testing
 */

import type { AuditLogEntry, AuditAction, AuditResourceType } from '@/types/session.types'
import { mediumOwner, mediumAdmin, mediumPartner1, mediumAssistant1, soloOwner } from './mockUsers'

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Generate a random audit ID
 */
function generateAuditId(): string {
  return `audit-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
}

/**
 * Get past date
 */
function getPastDate(hoursAgo: number): string {
  const date = new Date()
  date.setHours(date.getHours() - hoursAgo)
  return date.toISOString()
}

// ============================================================================
// MOCK AUDIT LOG ENTRIES
// ============================================================================

export const mockAuditLog: AuditLogEntry[] = [
  // Recent login
  {
    id: generateAuditId(),
    userId: mediumOwner.userId,
    userEmail: mediumOwner.email,
    performedByName: mediumOwner.fullName,
    action: 'login',
    resourceType: 'session',
    details: {
      loginMethod: 'password',
      deviceInfo: 'Chrome on Windows',
    },
    ipAddress: '103.21.125.45',
    userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 Chrome/120.0.0.0',
    country: 'India',
    city: 'Bangalore',
    success: true,
    createdAt: getPastDate(1),
  },

  // User created
  {
    id: generateAuditId(),
    userId: mediumOwner.userId,
    userEmail: mediumOwner.email,
    performedByName: mediumOwner.fullName,
    action: 'user_created',
    resourceType: 'user',
    resourceId: mediumAssistant1.userId,
    resourceName: mediumAssistant1.fullName,
    details: {
      targetUserId: mediumAssistant1.userId,
      targetUserEmail: mediumAssistant1.email,
      targetUserName: mediumAssistant1.fullName,
      newRole: 'assistant',
    },
    ipAddress: '103.21.125.45',
    success: true,
    createdAt: getPastDate(24),
  },

  // Role assigned
  {
    id: generateAuditId(),
    userId: mediumOwner.userId,
    userEmail: mediumOwner.email,
    performedByName: mediumOwner.fullName,
    action: 'role_assigned',
    resourceType: 'user',
    resourceId: mediumPartner1.userId,
    resourceName: mediumPartner1.fullName,
    details: {
      targetUserId: mediumPartner1.userId,
      targetUserEmail: mediumPartner1.email,
      oldRole: 'assistant',
      newRole: 'partner',
      reason: 'Promotion to Partner',
    },
    ipAddress: '103.21.125.45',
    success: true,
    createdAt: getPastDate(48),
  },

  // Failed login attempt
  {
    id: generateAuditId(),
    userId: undefined,
    userEmail: 'unknown@example.com',
    action: 'login_failed',
    resourceType: 'session',
    details: {
      loginMethod: 'password',
      reason: 'Invalid credentials',
      attemptedEmail: 'unknown@example.com',
    },
    ipAddress: '45.67.89.123',
    country: 'Unknown',
    success: false,
    errorMessage: 'Invalid email or password',
    createdAt: getPastDate(5),
  },

  // Settings updated
  {
    id: generateAuditId(),
    userId: mediumOwner.userId,
    userEmail: mediumOwner.email,
    performedByName: mediumOwner.fullName,
    action: 'settings_updated',
    resourceType: 'settings',
    details: {
      changedFields: ['firmPhone', 'firmEmail'],
      oldValues: {
        firmPhone: '9988776654',
        firmEmail: 'old@sharmapatel.co.in',
      },
      newValues: {
        firmPhone: '9988776655',
        firmEmail: 'info@sharmapatel.co.in',
      },
    },
    ipAddress: '103.21.125.45',
    success: true,
    createdAt: getPastDate(72),
  },

  // Password changed
  {
    id: generateAuditId(),
    userId: mediumAdmin.userId,
    userEmail: mediumAdmin.email,
    performedByName: mediumAdmin.fullName,
    action: 'password_changed',
    resourceType: 'user',
    resourceId: mediumAdmin.userId,
    details: {
      description: 'Password changed by user',
    },
    ipAddress: '122.166.89.112',
    success: true,
    createdAt: getPastDate(96),
  },

  // Session terminated
  {
    id: generateAuditId(),
    userId: mediumOwner.userId,
    userEmail: mediumOwner.email,
    performedByName: mediumOwner.fullName,
    action: 'session_terminated',
    resourceType: 'session',
    resourceId: 'session-revoked-001',
    details: {
      sessionId: 'session-revoked-001',
      terminatedUserId: mediumAdmin.userId,
      reason: 'Security concern',
    },
    ipAddress: '103.21.125.45',
    success: true,
    createdAt: getPastDate(120),
  },

  // Invitation sent
  {
    id: generateAuditId(),
    userId: mediumOwner.userId,
    userEmail: mediumOwner.email,
    performedByName: mediumOwner.fullName,
    action: 'invitation_sent',
    resourceType: 'invitation',
    details: {
      targetUserEmail: 'newuser@example.com',
      newRole: 'assistant',
      message: 'Welcome to Sharma & Patel!',
    },
    ipAddress: '103.21.125.45',
    success: true,
    createdAt: getPastDate(144),
  },

  // User deactivated
  {
    id: generateAuditId(),
    userId: mediumAdmin.userId,
    userEmail: mediumAdmin.email,
    performedByName: mediumAdmin.fullName,
    action: 'user_deactivated',
    resourceType: 'user',
    resourceId: 'user-former-001',
    resourceName: 'Former Employee',
    details: {
      targetUserId: 'user-former-001',
      targetUserEmail: 'former@sharmapatel.co.in',
      reason: 'Employee resignation',
    },
    ipAddress: '122.166.89.112',
    success: true,
    createdAt: getPastDate(168),
  },

  // Logout
  {
    id: generateAuditId(),
    userId: mediumPartner1.userId,
    userEmail: mediumPartner1.email,
    performedByName: mediumPartner1.fullName,
    action: 'logout',
    resourceType: 'session',
    details: {
      sessionId: 'session-partner-old',
    },
    ipAddress: '157.45.123.89',
    success: true,
    createdAt: getPastDate(200),
  },

  // Admin login
  {
    id: generateAuditId(),
    userId: mediumAdmin.userId,
    userEmail: mediumAdmin.email,
    performedByName: mediumAdmin.fullName,
    action: 'login',
    resourceType: 'session',
    details: {
      loginMethod: 'password',
      deviceInfo: 'Chrome on macOS',
    },
    ipAddress: '122.166.89.112',
    country: 'India',
    city: 'Bangalore',
    success: true,
    createdAt: getPastDate(220),
  },

  // Solo owner login
  {
    id: generateAuditId(),
    userId: soloOwner.userId,
    userEmail: soloOwner.email,
    performedByName: soloOwner.fullName,
    action: 'login',
    resourceType: 'session',
    details: {
      loginMethod: 'password',
      deviceInfo: 'Chrome on Windows',
    },
    ipAddress: '106.51.234.167',
    country: 'India',
    city: 'Pune',
    success: true,
    createdAt: getPastDate(240),
  },

  // All sessions terminated
  {
    id: generateAuditId(),
    userId: mediumOwner.userId,
    userEmail: mediumOwner.email,
    performedByName: mediumOwner.fullName,
    action: 'all_sessions_terminated',
    resourceType: 'session',
    details: {
      terminatedSessionCount: 3,
      reason: 'Security review',
    },
    ipAddress: '103.21.125.45',
    success: true,
    createdAt: getPastDate(300),
  },

  // Subscription changed
  {
    id: generateAuditId(),
    userId: mediumOwner.userId,
    userEmail: mediumOwner.email,
    performedByName: mediumOwner.fullName,
    action: 'subscription_changed',
    resourceType: 'subscription',
    details: {
      oldValues: { planCode: 'starter', billingCycle: 'monthly' },
      newValues: { planCode: 'professional', billingCycle: 'yearly' },
      description: 'Upgraded from Starter to Professional',
    },
    ipAddress: '103.21.125.45',
    success: true,
    createdAt: getPastDate(720),
  },

  // Tenant updated
  {
    id: generateAuditId(),
    userId: mediumOwner.userId,
    userEmail: mediumOwner.email,
    performedByName: mediumOwner.fullName,
    action: 'tenant_updated',
    resourceType: 'tenant',
    details: {
      changedFields: ['firmGstin', 'firmAddress'],
      description: 'Updated firm registration details',
    },
    ipAddress: '103.21.125.45',
    success: true,
    createdAt: getPastDate(1000),
  },
]

// ============================================================================
// HELPER FUNCTIONS FOR STORE
// ============================================================================

/**
 * Get all audit entries
 */
export function getAllAuditEntries(): AuditLogEntry[] {
  return [...mockAuditLog].sort((a, b) => compareRecordedDates(a.createdAt, b.createdAt, 'desc'))
}

/**
 * Get audit entries for a user
 */
export function getAuditEntriesForUser(userId: string): AuditLogEntry[] {
  return mockAuditLog
    .filter(e => e.userId === userId)
    .sort((a, b) => compareRecordedDates(a.createdAt, b.createdAt, 'desc'))
}

/**
 * Get audit entries by action
 */
export function getAuditEntriesByAction(action: AuditAction): AuditLogEntry[] {
  return mockAuditLog
    .filter(e => e.action === action)
    .sort((a, b) => compareRecordedDates(a.createdAt, b.createdAt, 'desc'))
}

/**
 * Get audit entries by resource type
 */
export function getAuditEntriesByResourceType(resourceType: AuditResourceType): AuditLogEntry[] {
  return mockAuditLog
    .filter(e => e.resourceType === resourceType)
    .sort((a, b) => compareRecordedDates(a.createdAt, b.createdAt, 'desc'))
}

/**
 * Get audit entries by date range
 */
export function getAuditEntriesByDateRange(dateFrom: string, dateTo: string): AuditLogEntry[] {
  const from = new Date(dateFrom).getTime()
  const to = new Date(dateTo).getTime()

  return mockAuditLog
    .filter(e => {
      const entryTime = recordedTime(e.createdAt) ?? NaN
      return entryTime >= from && entryTime <= to
    })
    .sort((a, b) => compareRecordedDates(a.createdAt, b.createdAt, 'desc'))
}

/**
 * Get failed audit entries
 */
export function getFailedAuditEntries(): AuditLogEntry[] {
  return mockAuditLog
    .filter(e => e.success === false)
    .sort((a, b) => compareRecordedDates(a.createdAt, b.createdAt, 'desc'))
}

/**
 * Get recent login attempts
 */
export function getRecentLoginAttempts(limit: number = 10): AuditLogEntry[] {
  return mockAuditLog
    .filter(e => e.action === 'login' || e.action === 'login_failed')
    .sort((a, b) => compareRecordedDates(a.createdAt, b.createdAt, 'desc'))
    .slice(0, limit)
}

/**
 * Add audit entry
 */
export function addAuditEntry(entry: Omit<AuditLogEntry, 'id' | 'createdAt'>): AuditLogEntry {
  const newEntry: AuditLogEntry = {
    ...entry,
    id: generateAuditId(),
    createdAt: new Date().toISOString(),
  }
  mockAuditLog.unshift(newEntry)
  return newEntry
}

// ============================================================================
// AUDIT STATISTICS
// ============================================================================

/**
 * Get audit statistics for admin dashboard
 */
export function getAuditStats(): {
  totalEntries: number
  successRate: number
  byAction: Array<{ action: AuditAction; count: number }>
  recentFailures: number
} {
  const totalEntries = mockAuditLog.length
  const successCount = mockAuditLog.filter(e => e.success).length
  const successRate = totalEntries > 0 ? (successCount / totalEntries) * 100 : 100

  // Count by action
  const actionCounts = new Map<AuditAction, number>()
  mockAuditLog.forEach(e => {
    actionCounts.set(e.action, (actionCounts.get(e.action) || 0) + 1)
  })

  const byAction = Array.from(actionCounts.entries())
    .map(([action, count]) => ({ action, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 5)

  // Count recent failures (last 24 hours)
  const oneDayAgo = new Date()
  oneDayAgo.setHours(oneDayAgo.getHours() - 24)
  const recentFailures = mockAuditLog.filter(
    e => e.success === false && (recordedTime(e.createdAt) ?? NaN) > oneDayAgo.getTime()
  ).length

  return {
    totalEntries,
    successRate: Math.round(successRate * 10) / 10,
    byAction,
    recentFailures,
  }
}

/**
 * Get login statistics
 */
export function getLoginStats(): {
  totalLogins: number
  failedLogins: number
  uniqueUsers: number
  topLocations: Array<{ city: string; count: number }>
} {
  const loginEntries = mockAuditLog.filter(e => e.action === 'login' || e.action === 'login_failed')

  const successfulLogins = loginEntries.filter(e => e.success)
  const failedLogins = loginEntries.filter(e => e.success === false)

  const uniqueUserIds = new Set(successfulLogins.map(e => e.userId).filter(Boolean))

  const locationCounts = new Map<string, number>()
  successfulLogins.forEach(e => {
    if (e.city) {
      locationCounts.set(e.city, (locationCounts.get(e.city) || 0) + 1)
    }
  })

  const topLocations = Array.from(locationCounts.entries())
    .map(([city, count]) => ({ city, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 5)

  return {
    totalLogins: successfulLogins.length,
    failedLogins: failedLogins.length,
    uniqueUsers: uniqueUserIds.size,
    topLocations,
  }
}
